import { all, call, delay, fork, put, select, takeLatest, takeLeading } from 'redux-saga/effects';
import { api } from '../../configurations/api';
import {
    connectedPlatformsUrl,
    instagramAutomationMediaUrl,
    instagramAutomationOAuthUrl,
    instagramAutomationPostUrl,
    instagramAutomationPostsUrl,
    instagramAutomationStatusUrl,
    socialLoginConfirmAuthUrl,
    socialPostsUrl,
} from '../../configurations/api/url';
import type {
    ConnectedPlatformRow,
    CreateSocialPostRequest,
    InstagramAutomationInput,
    InstagramAutomationMediaItem,
    InstagramAutomationStatusPayload,
    SocialPostWithAutomation,
} from '../../types/instagramAutomation.types';
import { SentryCapture } from '../../analytics/Sentry';
import { destroyOneCache, setCache } from '../cache/action';
import { CACHE_TYPE, CacheValue } from '../cache/types';
import { getCacheByType, getCacheDuration, isUseCacheEnabled } from '../cache/saga';
import { httpRequest } from '../types';
import { RootState } from '../root-reducer';
import { sanitizeCreateSocialPostBody } from '../../utils/sanitizeCreateSocialPostBody';
import {
    configureInstagramAutomationFail,
    configureInstagramAutomationSuccess,
    createSocialPostFail,
    createSocialPostSuccess,
    exchangeMetaOAuthFail,
    exchangeMetaOAuthSuccess,
    getConnectedPlatformsFail,
    getConnectedPlatformsSuccess,
    getInstagramAutomationOAuthUrlFail,
    getInstagramAutomationOAuthUrlSuccess,
    getInstagramAutomationStatusFail,
    getInstagramAutomationStatusSuccess,
    getSocialPostFail,
    getSocialPostSuccess,
    loadPostAutomationContextFail,
    loadPostAutomationContextSuccess,
    listInstagramAutomationPostsFail,
    listInstagramAutomationPostsSuccess,
    listInstagramAutomationMediaFail,
    listInstagramAutomationMediaSuccess,
    bindExistingIgAutomationFail,
    bindExistingIgAutomationSuccess,
    patchSocialPostFail,
    patchSocialPostSuccess,
    pollSocialPostUntilSettledFail,
    pollSocialPostUntilSettledSuccess,
    publishSocialPostFail,
    publishSocialPostFlowFail,
    publishSocialPostFlowSuccess,
    publishSocialPostSuccess,
} from './action';
import { types } from './types';
import {
    clearSocialPostPublishInFlight,
    getSocialPostErrorMessage,
    markSocialPostPublishInFlight,
    shouldPollAfterPublishError,
    SOCIAL_POST_POLL_INTERVAL_MS,
    SOCIAL_POST_POLL_MAX_MS,
    SOCIAL_POST_PUBLISH_DRAFT_GRACE_MS,
    SOCIAL_POST_PUBLISH_POLL_START_DELAY_MS,
    SOCIAL_POST_PUBLISH_REQUEST_TIMEOUT_SEC,
} from './socialPostPublishHelpers';

const POLL_INTERVAL_MS = SOCIAL_POST_POLL_INTERVAL_MS;
const POLL_MAX_MS = SOCIAL_POST_POLL_MAX_MS;

const DEFAULT_AUTOMATION_STATUS: InstagramAutomationStatusPayload = {
    feature_enabled: false,
    instagram_connected: false,
    automation_scopes_granted: false,
    needs_reconnect: false,
    has_influencer_profile: false,
};

function getErrorMessage(error: any, fallback: string): string {
    return getSocialPostErrorMessage(error, fallback);
}

function* isInstagramCommentAutomationEnabled(): any {
    const state: RootState = yield select((s: RootState) => s);
    const globalOn =
        state.configuration?.globalConfiguration?.buzzbridge?.instagram_comment_automation_enabled === true;
    const apiOn = Boolean(state.socialPosts?.automationStatus?.feature_enabled);
    return globalOn && apiOn;
}

function* stripInstagramAutomationFromCreateBody(body: CreateSocialPostRequest): any {
    const enabled = yield* isInstagramCommentAutomationEnabled();
    if (enabled || !body.instagram_automation) {
        return body;
    }
    const { instagram_automation: _removed, ...rest } = body;
    return rest as CreateSocialPostRequest;
}

function* stripInstagramAutomationFromPatch(patch: Record<string, unknown>): any {
    const enabled = yield* isInstagramCommentAutomationEnabled();
    if (enabled || !patch.instagram_automation) {
        return patch;
    }
    const { instagram_automation: _removed, ...rest } = patch;
    return rest;
}

function* readCachedValue(cacheType: CACHE_TYPE): any {
    const defaultUseCache = yield* isUseCacheEnabled();
    const defaultCacheDuration = yield* getCacheDuration();
    const cache: CacheValue = yield* getCacheByType(cacheType);

    if (
        cache?.key &&
        defaultUseCache &&
        ((Date.now() - Number(cache.key)) as unknown as number) < defaultCacheDuration
    ) {
        return cache.value;
    }
    return null;
}

function* fetchConnectedPlatformsFromApi(): any {
    const response = yield call(api, connectedPlatformsUrl, httpRequest.GET, null, 2);
    const platforms = response?.data?.platforms ?? [];
    const hasInfluencerProfile = Boolean(response?.data?.has_influencer_profile);
    // Keep automation status flag in sync so settings modals flip create → update.
    if (hasInfluencerProfile) {
        const state: RootState = yield select((s: RootState) => s);
        const current = state.socialPosts?.automationStatus;
        if (current && !current.has_influencer_profile) {
            yield put(
                getInstagramAutomationStatusSuccess({
                    ...current,
                    has_influencer_profile: true,
                }),
            );
        }
    }
    return platforms;
}

function* fetchAutomationStatusFromApi(): any {
    const response = yield call(api, instagramAutomationStatusUrl, httpRequest.GET, null, 2);
    return response?.data ?? DEFAULT_AUTOMATION_STATUS;
}

function* createPostFromApi(body: CreateSocialPostRequest): any {
    const sanitized = sanitizeCreateSocialPostBody(body);
    const response = yield call(api, socialPostsUrl, httpRequest.POST, sanitized, 2);
    if (!response?.data?.post) {
        throw new Error('Invalid create post response');
    }
    return response.data.post as SocialPostWithAutomation;
}

function* publishPostFromApi(postId: string): any {
    // Short client timeout — do not block on gateway 504 while Meta transcodes.
    const response = yield call(
        api,
        `${socialPostsUrl}/${postId}/publish`,
        httpRequest.POST,
        {},
        0,
        SOCIAL_POST_PUBLISH_REQUEST_TIMEOUT_SEC,
    );
    if (!response?.data?.post) {
        throw new Error('Invalid publish post response');
    }
    return response.data.post as SocialPostWithAutomation;
}

/** Fire POST /publish in the background; polling is the source of truth for UI. */
function* publishPostFireAndForgetBackground(postId: string): any {
    try {
        yield call(publishPostFromApi, postId);
    } catch (error) {
        if (!shouldPollAfterPublishError(error)) {
            SentryCapture(error, 'warning');
        }
    }
}

function* waitForPublishToStart(postId: string): any {
    yield delay(SOCIAL_POST_PUBLISH_POLL_START_DELAY_MS);

    let post: SocialPostWithAutomation = yield call(fetchPostFromApi, postId);
    if (post.status === 'published') return post;
    if (post.status === 'failed') {
        throw new Error(post.error_message ?? 'Publishing failed.');
    }
    if (post.status === 'publishing') {
        return yield call(pollPostUntilSettled, postId);
    }

    // Still draft — publish may be slow to flip status
    yield delay(SOCIAL_POST_PUBLISH_DRAFT_GRACE_MS);
    post = yield call(fetchPostFromApi, postId);
    if (post.status === 'published') return post;
    if (post.status === 'failed') {
        throw new Error(post.error_message ?? 'Publishing failed.');
    }
    if (post.status === 'draft') {
        throw new Error('Unable to start publishing. Please try again.');
    }

    return yield call(pollPostUntilSettled, postId);
}

function* publishPostOnceThenPoll(postId: string): any {
    if (!markSocialPostPublishInFlight(postId)) {
        return yield call(pollPostUntilSettled, postId);
    }

    try {
        // Fire-and-forget: kick off publish once, then poll — never await gateway 504.
        yield fork(publishPostFireAndForgetBackground, postId);
        return yield call(waitForPublishToStart, postId);
    } finally {
        clearSocialPostPublishInFlight(postId);
    }
}

function* fetchPostFromApi(postId: string): any {
    const response = yield call(api, `${socialPostsUrl}/${postId}`, httpRequest.GET, null, 2);
    if (!response?.data?.post) {
        throw new Error('Invalid get post response');
    }
    return response.data.post as SocialPostWithAutomation;
}

function* pollPostUntilSettled(postId: string): any {
    const started = Date.now();
    let last: SocialPostWithAutomation = yield call(fetchPostFromApi, postId);

    while (Date.now() - started < POLL_MAX_MS) {
        if (last.status === 'published' || last.status === 'failed') {
            return last;
        }
        yield delay(POLL_INTERVAL_MS);
        try {
            last = yield call(fetchPostFromApi, postId);
        } catch {
            // Transient network error — keep the last known post and continue polling
        }
    }

    if (last.status === 'published' || last.status === 'failed') {
        return last;
    }

    throw new Error(
        last.status === 'publishing'
            ? 'Publishing is taking longer than expected. Check your connected account shortly.'
            : 'Publishing did not complete. Please refresh and try again.',
    );
}

function* configureAutomationFromApi(socialPostId: string, config: InstagramAutomationInput): any {
    yield call(api, instagramAutomationPostUrl(socialPostId), httpRequest.PUT, config, 2);
    return yield call(fetchPostFromApi, socialPostId);
}

function* getConnectedPlatformsSaga({ payload, resolve, reject }: any): any {
    const skipRefresh = payload?.skipRefresh;

    if (!skipRefresh) {
        const cached = yield* readCachedValue(CACHE_TYPE.SOCIAL_CONNECTED_PLATFORMS);
        if (cached) {
            yield put(getConnectedPlatformsSuccess(cached));
            resolve?.(cached);
            return;
        }
    }

    try {
        const platforms = yield call(fetchConnectedPlatformsFromApi);
        yield put(setCache({ key: Date.now(), value: platforms, type: CACHE_TYPE.SOCIAL_CONNECTED_PLATFORMS }));
        yield put(getConnectedPlatformsSuccess(platforms));
        resolve?.(platforms);
    } catch (error) {
        SentryCapture(error, 'error');
        const message = getErrorMessage(error, 'Unable to load connected platforms.');
        yield put(getConnectedPlatformsFail(message as any));
        reject?.(error);
    }
}

function* getInstagramAutomationStatusSaga({ payload, resolve, reject }: any): any {
    const skipRefresh = payload?.skipRefresh;

    if (!skipRefresh) {
        const cached = yield* readCachedValue(CACHE_TYPE.INSTAGRAM_AUTOMATION_STATUS);
        if (cached) {
            yield put(getInstagramAutomationStatusSuccess(cached));
            resolve?.(cached);
            return;
        }
    }

    try {
        const status = yield call(fetchAutomationStatusFromApi);
        yield put(setCache({ key: Date.now(), value: status, type: CACHE_TYPE.INSTAGRAM_AUTOMATION_STATUS }));
        yield put(getInstagramAutomationStatusSuccess(status));
        resolve?.(status);
    } catch (error) {
        SentryCapture(error, 'error');
        const message = getErrorMessage(error, 'Unable to load automation status.');
        yield put(getInstagramAutomationStatusFail(message as any));
        reject?.(error);
    }
}

function* loadPostAutomationContextSaga({ payload, resolve, reject }: any): any {
    const skipRefresh = payload?.skipRefresh;

    try {
        let automationStatus: InstagramAutomationStatusPayload | null = null;

        if (!skipRefresh) {
            const cachedStatus = yield* readCachedValue(CACHE_TYPE.INSTAGRAM_AUTOMATION_STATUS);
            if (cachedStatus) {
                automationStatus = cachedStatus;
            }
        }

        if (!automationStatus || skipRefresh) {
            try {
                automationStatus = yield call(fetchAutomationStatusFromApi);
                yield put(
                    setCache({
                        key: Date.now(),
                        value: automationStatus,
                        type: CACHE_TYPE.INSTAGRAM_AUTOMATION_STATUS,
                    }),
                );
            } catch {
                automationStatus = DEFAULT_AUTOMATION_STATUS;
            }
        }

        yield put(getInstagramAutomationStatusSuccess(automationStatus!));

        let connectedPlatforms: ConnectedPlatformRow[] | null = null;

        if (!skipRefresh) {
            const cachedPlatforms = yield* readCachedValue(CACHE_TYPE.SOCIAL_CONNECTED_PLATFORMS);
            if (cachedPlatforms) {
                connectedPlatforms = cachedPlatforms;
            }
        }

        if (!connectedPlatforms || skipRefresh) {
            connectedPlatforms = yield call(fetchConnectedPlatformsFromApi);
            yield put(
                setCache({ key: Date.now(), value: connectedPlatforms, type: CACHE_TYPE.SOCIAL_CONNECTED_PLATFORMS }),
            );
        }

        yield put(getConnectedPlatformsSuccess(connectedPlatforms!));

        const result = { automationStatus: automationStatus!, connectedPlatforms: connectedPlatforms! };
        yield put(loadPostAutomationContextSuccess(result));
        resolve?.(result);
    } catch (error) {
        SentryCapture(error, 'error');
        const message = getErrorMessage(error, 'Unable to load post automation.');
        yield put(loadPostAutomationContextFail(message as any));
        reject?.(error);
    }
}

function* getInstagramAutomationOAuthUrlSaga({ resolve, reject }: any): any {
    try {
        const enabled = yield* isInstagramCommentAutomationEnabled();
        if (!enabled) {
            throw new Error('Instagram comment automation is not enabled.');
        }
        const response = yield call(api, instagramAutomationOAuthUrl, httpRequest.GET, null, 2);
        const url = response?.data?.url ?? '';
        yield put(getInstagramAutomationOAuthUrlSuccess(url));
        resolve?.(url);
    } catch (error) {
        SentryCapture(error, 'error');
        const message = getErrorMessage(error, 'Unable to get OAuth URL.');
        yield put(getInstagramAutomationOAuthUrlFail(message as any));
        reject?.(error);
    }
}

function* exchangeMetaOAuthSaga({ payload, resolve, reject }: any): any {
    try {
        const enabled = yield* isInstagramCommentAutomationEnabled();
        if (!enabled) {
            throw new Error('Instagram comment automation is not enabled.');
        }
        yield call(api, `${socialLoginConfirmAuthUrl}/exchange`, httpRequest.POST, {
            provider: 'meta',
            code: payload.code,
        });
        yield put(exchangeMetaOAuthSuccess());
        yield put(destroyOneCache({ cacheType: CACHE_TYPE.SOCIAL_CONNECTED_PLATFORMS }));
        yield put(destroyOneCache({ cacheType: CACHE_TYPE.INSTAGRAM_AUTOMATION_STATUS }));
        resolve?.();
    } catch (error) {
        SentryCapture(error, 'error');
        const message = getErrorMessage(error, 'Failed to connect Meta account.');
        yield put(exchangeMetaOAuthFail(message as any));
        reject?.(error);
    }
}

function* createSocialPostSaga({ payload, resolve, reject }: any): any {
    try {
        const body = yield* stripInstagramAutomationFromCreateBody(payload.body);
        const post = yield call(createPostFromApi, body);
        yield put(createSocialPostSuccess(post));
        resolve?.(post);
    } catch (error) {
        SentryCapture(error, 'error');
        const message = getErrorMessage(error, 'Failed to create social post.');
        yield put(createSocialPostFail(message as any));
        reject?.(error);
    }
}

function* publishSocialPostSaga({ payload, resolve, reject }: any): any {
    try {
        const post = yield call(publishPostOnceThenPoll, payload.postId);
        if (post.status === 'failed') {
            throw new Error(post.error_message ?? 'Publishing failed.');
        }
        yield put(publishSocialPostSuccess(post));
        resolve?.(post);
    } catch (error) {
        SentryCapture(error, 'error');
        const message = getErrorMessage(error, 'Failed to publish social post.');
        yield put(publishSocialPostFail(message as any));
        reject?.(error);
    }
}

function* getSocialPostSaga({ payload, resolve, reject }: any): any {
    try {
        const post = yield call(fetchPostFromApi, payload.postId);
        yield put(getSocialPostSuccess(post));
        resolve?.(post);
    } catch (error) {
        SentryCapture(error, 'error');
        const message = getErrorMessage(error, 'Failed to fetch social post.');
        yield put(getSocialPostFail(message as any));
        reject?.(error);
    }
}

function* configureInstagramAutomationSaga({ payload, resolve, reject }: any): any {
    try {
        const enabled = yield* isInstagramCommentAutomationEnabled();
        if (!enabled) {
            throw new Error('Instagram comment automation is not enabled.');
        }
        const post = yield call(configureAutomationFromApi, payload.socialPostId, payload.config);
        yield put(configureInstagramAutomationSuccess(post));
        resolve?.(post);
    } catch (error) {
        SentryCapture(error, 'error');
        const message = getErrorMessage(error, 'Failed to configure Instagram automation.');
        yield put(configureInstagramAutomationFail(message as any));
        reject?.(error);
    }
}

function* publishSocialPostFlowSaga({ payload, resolve, reject }: any): any {
    try {
        const body = yield* stripInstagramAutomationFromCreateBody(payload.body);
        let post: SocialPostWithAutomation = yield call(createPostFromApi, body);

        if (post.status === 'scheduled' || body.scheduled_at) {
            yield put(publishSocialPostFlowSuccess(post));
            resolve?.(post);
            return;
        }

        if (post.status === 'draft' || post.status === 'failed') {
            post = yield call(publishPostOnceThenPoll, post._id);
        } else if (post.status === 'publishing') {
            post = yield call(pollPostUntilSettled, post._id);
        }

        if (post.status === 'failed') {
            throw new Error(post.error_message ?? 'Publishing failed.');
        }

        if (payload.configureAutomation && post.platform === 'instagram') {
            const enabled = yield* isInstagramCommentAutomationEnabled();
            if (enabled) {
                post = yield call(configureAutomationFromApi, post._id, payload.configureAutomation);
            }
        }

        yield put(publishSocialPostFlowSuccess(post));
        resolve?.(post);
    } catch (error) {
        SentryCapture(error, 'error');
        const message = getErrorMessage(error, 'Failed to publish social post.');
        yield put(publishSocialPostFlowFail(message as any));
        reject?.(error);
    }
}

function* patchPostFromApi(postId: string, patch: Record<string, unknown>): any {
    const response = yield call(api, `${socialPostsUrl}/${postId}`, httpRequest.PATCH, patch, 2);
    if (!response?.data?.post) {
        throw new Error('Invalid patch post response');
    }
    return response.data.post as SocialPostWithAutomation;
}

function* listInstagramAutomationPostsSaga({ payload, resolve, reject }: any): any {
    const page = payload?.page ?? 1;
    const limit = payload?.limit ?? 50;
    const skipRefresh = payload?.skipRefresh;

    if (!skipRefresh) {
        const cached = yield* readCachedValue(CACHE_TYPE.INSTAGRAM_AUTOMATION_POSTS);
        if (cached) {
            yield put(listInstagramAutomationPostsSuccess(cached));
            resolve?.(cached);
            return;
        }
    }

    try {
        const response = yield call(
            api,
            `${socialPostsUrl}?platform=instagram&page=${page}&limit=${limit}`,
            httpRequest.GET,
            null,
            2,
        );
        const posts: SocialPostWithAutomation[] = response?.data?.posts ?? [];
        const result = {
            posts,
            totalCount: response?.totalCount ?? posts.length,
            currentPage: response?.currentPage ?? page,
            totalPages: response?.totalPages ?? 1,
        };
        yield put(
            setCache({
                key: Date.now(),
                value: result,
                type: CACHE_TYPE.INSTAGRAM_AUTOMATION_POSTS,
                isUnique: true,
            }),
        );
        yield put(listInstagramAutomationPostsSuccess(result));
        resolve?.(result);
    } catch (error) {
        SentryCapture(error, 'error');
        const message = getErrorMessage(error, 'Unable to load Instagram automations.');
        yield put(listInstagramAutomationPostsFail(message as any));
        reject?.(error);
    }
}

function* patchSocialPostSaga({ payload, resolve, reject }: any): any {
    try {
        const patch = yield* stripInstagramAutomationFromPatch(payload.patch);
        const post = yield call(patchPostFromApi, payload.postId, patch);
        yield put(patchSocialPostSuccess(post));
        yield put(destroyOneCache({ cacheType: CACHE_TYPE.INSTAGRAM_AUTOMATION_POSTS }));
        resolve?.(post);
    } catch (error) {
        SentryCapture(error, 'error');
        const message = getErrorMessage(error, 'Failed to update social post.');
        yield put(patchSocialPostFail({ postId: payload.postId, error: message }));
        reject?.(error);
    }
}

function* listInstagramAutomationMediaSaga({ payload, resolve, reject }: any): any {
    try {
        const limit = payload?.limit ?? 5;
        const after = payload?.after;
        const response = yield call(api, instagramAutomationMediaUrl(limit, after), httpRequest.GET, null, 2);
        const media: InstagramAutomationMediaItem[] = response?.data?.media ?? [];
        const paging = response?.data?.paging ?? {
            next_cursor: null,
            has_more: false,
        };
        yield put(listInstagramAutomationMediaSuccess(media));
        resolve?.({ media, paging });
    } catch (error) {
        SentryCapture(error, 'error');
        const message = getErrorMessage(error, 'Unable to load Instagram posts.');
        yield put(listInstagramAutomationMediaFail(message as any));
        reject?.(error);
    }
}

function* bindExistingIgAutomationSaga({ payload, resolve, reject }: any): any {
    try {
        const response = yield call(api, instagramAutomationPostsUrl, httpRequest.POST, payload.body, 2);
        const post: SocialPostWithAutomation = response?.data?.post ?? response?.data?.data?.post ?? response?.post;
        if (!post?._id) {
            throw new Error('Bind automation succeeded but no post was returned.');
        }
        if (!post.instagram_automation && response?.data?.automation) {
            post.instagram_automation = response.data.automation;
        }
        yield put(bindExistingIgAutomationSuccess(post));
        yield put(destroyOneCache({ cacheType: CACHE_TYPE.INSTAGRAM_AUTOMATION_POSTS }));
        resolve?.(post);
    } catch (error) {
        SentryCapture(error, 'error');
        const message = getErrorMessage(error, 'Failed to create automation for that post.');
        yield put(bindExistingIgAutomationFail(message as any));
        reject?.(error);
    }
}

function* pollSocialPostUntilSettledSaga({ payload, resolve, reject }: any): any {
    try {
        const post = yield call(pollPostUntilSettled, payload.postId);
        yield put(pollSocialPostUntilSettledSuccess(post));
        yield put(getSocialPostSuccess(post));
        resolve?.(post);
    } catch (error) {
        SentryCapture(error, 'error');
        const message = getErrorMessage(error, 'Failed while waiting for publish.');
        yield put(pollSocialPostUntilSettledFail(message as any));
        reject?.(error);
    }
}

function* getConnectedPlatformsWatcher() {
    yield takeLatest(types.GET_CONNECTED_PLATFORMS, getConnectedPlatformsSaga);
}

function* getInstagramAutomationStatusWatcher() {
    yield takeLatest(types.GET_INSTAGRAM_AUTOMATION_STATUS, getInstagramAutomationStatusSaga);
}

function* loadPostAutomationContextWatcher() {
    yield takeLatest(types.LOAD_POST_AUTOMATION_CONTEXT, loadPostAutomationContextSaga);
}

function* getInstagramAutomationOAuthUrlWatcher() {
    yield takeLatest(types.GET_INSTAGRAM_AUTOMATION_OAUTH_URL, getInstagramAutomationOAuthUrlSaga);
}

function* exchangeMetaOAuthWatcher() {
    yield takeLatest(types.EXCHANGE_META_OAUTH, exchangeMetaOAuthSaga);
}

function* createSocialPostWatcher() {
    yield takeLatest(types.CREATE_SOCIAL_POST, createSocialPostSaga);
}

function* publishSocialPostWatcher() {
    yield takeLeading(types.PUBLISH_SOCIAL_POST, publishSocialPostSaga);
}

function* getSocialPostWatcher() {
    yield takeLatest(types.GET_SOCIAL_POST, getSocialPostSaga);
}

function* configureInstagramAutomationWatcher() {
    yield takeLatest(types.CONFIGURE_INSTAGRAM_AUTOMATION, configureInstagramAutomationSaga);
}

function* listInstagramAutomationPostsWatcher() {
    yield takeLatest(types.LIST_INSTAGRAM_AUTOMATION_POSTS, listInstagramAutomationPostsSaga);
}

function* patchSocialPostWatcher() {
    yield takeLatest(types.PATCH_SOCIAL_POST, patchSocialPostSaga);
}

function* listInstagramAutomationMediaWatcher() {
    yield takeLatest(types.LIST_INSTAGRAM_AUTOMATION_MEDIA, listInstagramAutomationMediaSaga);
}

function* bindExistingIgAutomationWatcher() {
    yield takeLeading(types.BIND_EXISTING_IG_AUTOMATION, bindExistingIgAutomationSaga);
}

function* pollSocialPostUntilSettledWatcher() {
    yield takeLatest(types.POLL_SOCIAL_POST_UNTIL_SETTLED, pollSocialPostUntilSettledSaga);
}

function* publishSocialPostFlowWatcher() {
    yield takeLeading(types.PUBLISH_SOCIAL_POST_FLOW, publishSocialPostFlowSaga);
}

export default function* socialPostsSaga() {
    yield all([
        getConnectedPlatformsWatcher(),
        getInstagramAutomationStatusWatcher(),
        loadPostAutomationContextWatcher(),
        getInstagramAutomationOAuthUrlWatcher(),
        exchangeMetaOAuthWatcher(),
        createSocialPostWatcher(),
        publishSocialPostWatcher(),
        getSocialPostWatcher(),
        configureInstagramAutomationWatcher(),
        listInstagramAutomationPostsWatcher(),
        patchSocialPostWatcher(),
        listInstagramAutomationMediaWatcher(),
        bindExistingIgAutomationWatcher(),
        pollSocialPostUntilSettledWatcher(),
        publishSocialPostFlowWatcher(),
    ]);
}
